import { sanitizeReturnTo } from '@auth/utils/sanitizeReturnTo';

const LEGACY_REDIRECT_URL_PARAM = 'redirectUrl';
const CANONICAL_RETURN_TO_PARAM = 'returnTo';

const tryAtob = (raw: string): string | null => {
  try {
    return typeof atob === 'function' ? atob(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Decode a legacy `?redirectUrl=` value the same way `safelyDecode` did:
 *   - try `atob` when the input doesn't already look like a URL
 *   - fall back to the raw string non-base64 input
 *   - return null only when there's nothing to decode
 */
export const decodeLegacyRedirectUrl = (raw: string | null): string | null => {
  if (raw === null || raw === '') {
    return null;
  }
  if (raw.startsWith('/') || raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }
  return tryAtob(raw) ?? raw;
};

/**
 * Resolves a returnTo URL from a URLSearchParams instance, accepting BOTH:
 *
 *   1. The new canonical `?returnTo=<plain url>`
 *   2. The legacy `?redirectUrl=<btoa(url)>` format
 */
export const resolveReturnToFromUrl = (
  search: URLSearchParams,
  fallbackUrl: string,
): string => {
  const canonical = search.get(CANONICAL_RETURN_TO_PARAM);
  if (canonical) {
    return sanitizeReturnTo(canonical, fallbackUrl);
  }

  const legacy = decodeLegacyRedirectUrl(search.get(LEGACY_REDIRECT_URL_PARAM));
  if (legacy) {
    return sanitizeReturnTo(legacy, fallbackUrl);
  }

  return fallbackUrl;
};

/**
 * Resolves a returnTo URL from a full URL string (or `window.location`-style object)
 */
export const resolveReturnToFromHref = (
  href: string | undefined,
  fallbackUrl: string,
): string => {
  if (!href) {
    return fallbackUrl;
  }
  try {
    const url = new URL(href);
    return resolveReturnToFromUrl(url.searchParams, fallbackUrl);
  } catch {
    return fallbackUrl;
  }
};

/**
 * Resolves a returnTo URL from `appState.redirectUrl`.
 * Ensures legacy redirectUrl values are properly handled
 */
export const resolveReturnToFromAppState = (appState: unknown): string | null => {
  if (!appState || typeof appState !== 'object') {
    return null;
  }
  const state = appState as Record<string, unknown>;

  if (typeof state.returnTo === 'string' && state.returnTo) {
    return sanitizeReturnTo(state.returnTo, '') || null;
  }

  if (typeof state.redirectUrl === 'string' && state.redirectUrl) {
    const decoded = decodeLegacyRedirectUrl(state.redirectUrl);
    if (decoded) {
      return sanitizeReturnTo(decoded, '') || null;
    }
  }

  return null;
};
